import { InstrumentManifestType } from '@utils/nf-webaudio/instrument-manifest'
import { NoteTime, Note, Sequence, ScheduleNote } from '@utils/nf-webaudio/sequencer.types'
import { SynthesisContextType } from '@utils/nf-webaudio/synthesis-context'

import nfsyn from './nf-webaudio'

export interface SequencerOptions {
  loopStart?: number // Time at which to start loop. If not present or not a valid time offset, looping is disabled - In ticks
  loopEnd?: number // End time of loop. If not present, the end of the last note in the sequence is used - In ticks
  loopLimit?: number | undefined // Number of times to play the loop. If not set, looping continues indefinitely.
}

export interface SequencerType {
  startPlaying: () => void
  prepareElementsToPlay: () => void
  playNextWindow: (eow?: number) => void
  haltPlayback: () => void
  scheduleElement: (note: Note, noteStart: number, noteEnd: number) => void
  scheduleNote: (note: Note, noteStart: number, noteEnd: number) => void
  stop: () => void
  synth: SynthesisContextType
  manifest: InstrumentManifestType
  sequence: Sequence
  scheduledNotes: ScheduleNote[]
  noteTimes: NoteTime[]
  endTick: number
  playWindow: number
  playWindowInterval: number
  playbackSpeed: number
  pitchShift: number
  sequenceStart: number
  sequenceEnd: number
  playing: boolean
  premium: boolean
  options: SequencerOptions
  loopLimit: number
  loopStart: number
  loopEnd: number
  startingLoopRepetition: number
  playWindowHandle: NodeJS.Timer
  startTime: number
  loopStartRestartNotes: { noteTimes: NoteTime; note: Note }[]
  index: number
  loopCount: number
  loopTimeShift: number
  loopEndIndex: number
  loopStartIndex: number
}

// Representation of a time-based sequencer that can play Noteflight "performances".

// Within a Sequencer, there are at least five different temporal frames of reference (ways in
// which a number might represent a time):
//
// (1) Score position, in ticks (256th notes).
//
// (2) The time in seconds from the beginning of the score, if playbackSpeed were 1 and looping
//     disabled; derived from (1) by calling scorePositionToTime. During playback, this value
//     changes at a rate that depends on playbackSpeed, and will decrease when jumping back from
//     the end to the beginning of a loop. This is the value returned by sequencer.timeOffset()
//     (well, except that sequencer.timeOffset() also tries to compensate for output latency).
//
// (3) The time in seconds from the beginning of the score, taking playbackSpeed into account but
//     not looping; derived from (2) by multiplying by playbackSpeed (which is actually the
//     reciprocal of the playback speed; 2 means half speed). This value always increases at a
//     rate of 1 per second during playback, except when it hits the end of a loop; then it jumps
//     back to the start of the loop.
//
// (4) The time in seconds from the beginning of the score, taking playbackSpeed and looping into
//     account; derived from (3) by adding in the loops that have happened so far. This number
//     always increases by 1 per second, as long as playback is happening.
//
// (5) The real time according to synth.context.currentTime, which is (4) plus the time at which
//     playback started. This increases by 1 per second even when playback is not happening.
const Sequencer = function (
  this: SequencerType,
  synth: SynthesisContextType, // The global InstrumentManifest. TODO: unused.
  manifest: InstrumentManifestType, // The global InstrumentManifest. TODO: unused.
  sequence: Sequence,
  opt_options?: SequencerOptions
): void {
  this.synth = synth
  this.manifest = manifest
  this.sequence = sequence

  if (!this.synth) {
    console.error('Sequencer cannot find Synthesis Context')
    return
  }

  if (!this.manifest) {
    console.error('Sequencer cannot find Instrument Manifest')
    return
  }

  if (!this.sequence) {
    console.error('Sequencer cannot find temporal map')
    return
  }

  // store scheduled notes for garbage collection (chrome42)
  this.scheduledNotes = []

  const endTick = 0

  // Array that corresponds element for element with the sequence's
  // array of notes. Contains startTime and endTime for each note,
  // in reference frame (2).
  //
  // If the sequence is an audio preview JSON, this array will simply be set
  // to the noteTimes object that is part of the JSON.
  this.noteTimes = sequence.noteTimes

  // Time in ticks of the end of the last note to end (not necessarily the last note in the list).
  this.endTick = endTick
  this.playWindow = 2 // window of synthesis-time rendered in a given rendering callback
  this.playWindowInterval = 0.5 // time interval for rendering callback
  this.playbackSpeed = 1 // inverse multiplicative factor for playback speed
  this.pitchShift = 0 // additive pitch shift for note ordinals
  this.sequenceStart = 0 // start synthesis-time for playback, will be mapped to SynthContext's startTime in real time
  this.sequenceEnd = 0 // end synthesis-time
  this.playing = false
  this.premium = true // use premium instruments
  this.options = opt_options || {}
  // Figure out loop parameters. Use defaults if any parameter is not specified.

  // No looping
  this.loopLimit = 0
  this.loopStart = 0
  this.loopEnd = 0
  this.startingLoopRepetition = 0

  // nfsyn.Sequencer traditionally assumed that it was initialized on the
  // Score Editor page, which caused it to call `scoreMixBinding`, which sets
  // the gain levels, etc., for the instruments.
  // If nfsyn.Sequencer is initialized on any other page, we have to set the
  // gain levels, etc., manually. In this case, we include the relevant
  // values in the representation of the performance and manually set the
  // gain levels, etc.
  if (sequence.scoreMix) {
    this.synth.setMasterGain(sequence.scoreMix.masterGain)
    this.synth.applyReverbImpulse()
    this.synth.setReverbLevel(sequence.scoreMix.reverbLevel)
  }
}

Sequencer.prototype.stopPlayback = function () {
  this.haltPlayback()
  this.sequence.parts.forEach((part) => {
    part.synthesizer.muteScheduledNotes()
  })
}

// // Stop this sequencer's playback cleanly
Sequencer.prototype.stop = function () {
  this.stopPlayback()
  this.synth.paused = false
}

// Set StartTime value
Sequencer.prototype.prepareElementsToPlay = function (this: SequencerType) {
  // Initialize start time in the near future relative to synth context time.
  // The slight delay gives some WebAudio impls (incl. versions of FF) a chance to "warm up".
  this.startTime = this.synth.context.currentTime + nfsyn.WARM_UP_DELAY

  // Less efficient way. The values above should be more efficient, but didn't work. TODO: debug.
  this.startTime -=
    (this.sequenceStart + (this.loopEnd - this.loopStart) * this.startingLoopRepetition) *
    this.playbackSpeed

  this.loopTimeShift = 0
  this.loopCount = 1

  this.index = 0
  this.loopStartRestartNotes = []
}

// Start playing this sequence; assumes that all instruments are loaded.
Sequencer.prototype.startPlaying = function (this: SequencerType) {
  if (!this.synth.active) {
    return
  }

  this.prepareElementsToPlay()

  this.playWindowHandle = setInterval(() => this.playNextWindow(), this.playWindowInterval * 1000)
  this.playNextWindow()

  this.playing = true
}

Sequencer.prototype.haltPlayback = function (this: SequencerType) {
  if (this.playing) {
    clearInterval(this.playWindowHandle)
    this.playing = false // TODO: there should be a flag that indicates that previously scheduled sound is still active, but this isn't that
  }
}

// Render a single block of contiguous notes scheduled to play in the near future, stopping playback rendering if needed.
Sequencer.prototype.playNextWindow = function (this: SequencerType, opt_eow?: number): void {
  const elements = this.sequence.performance.elements
  const endOfWindow = opt_eow
    ? opt_eow
    : this.synth.context.currentTime - this.startTime + this.playWindow

  // garbage collection - disconnect notes that have already been played (chrome42)
  let j = 0
  while (j < this.scheduledNotes.length) {
    const n = this.scheduledNotes[j]
    if (n.endTime < this.synth.context.currentTime) {
      n.gainNode.disconnect()
      this.scheduledNotes.splice(j, 1)
    } else {
      j++
    }
  }

  const newLocal = true
  while (newLocal) {
    // Stop playing if no more notes.
    if (elements === null) continue
    if (this.index >= elements.length && this.loopCount >= this.loopLimit) {
      this.haltPlayback()
      return
    }

    // get the next note
    let note: Note | null = elements[this.index]
    const noteTimes = this.noteTimes[this.index] // notetimes.startTime and .endTime are in frame (2).

    if (
      note &&
      this.loopLimit &&
      noteTimes.startTime >= this.loopEnd &&
      this.loopCount < this.loopLimit
    ) {
      // Weird edge case. Don't process the note (yet) if it's after the loop and we're not done
      // looping yet... this can happen if there are no notes that start inside the loop.
      note = null
    }

    if (note) {
      const noteStart = noteTimes.startTime * this.playbackSpeed // reference frame (3).
      // If we've exceeded the time window for this chunk of rendering, wait for the timer to call us again
      if (noteStart + this.loopTimeShift > endOfWindow) {
        // frame (4).
        return
      }
      let noteEnd = noteTimes.endTime * this.playbackSpeed // frame (3).

      if (this.loopLimit) {
        // Deal with a couple special cases. Note that both of these can apply on the same note.

        // Comparisons are in reference frame (2). The results are translated to reference frame (3).

        // Case (a): If the note starts before and ends after the START of the loop, put it in a list to be
        // restarted each time the loop restarts.
        if (noteTimes.startTime < this.loopStart && noteTimes.endTime > this.loopStart) {
          this.loopStartRestartNotes.push({ noteTimes: noteTimes, note: note })
        }
        // Case (b): If the note starts before and ends after the END of the loop, truncate it to
        // end at the end of the loop, except on the last time through.
        if (
          noteTimes.startTime < this.loopEnd &&
          noteTimes.endTime > this.loopEnd &&
          this.loopCount < this.loopLimit
        ) {
          noteEnd = this.loopEnd * this.playbackSpeed
        }
      }

      // Stop playing if we hit the sequence range endpoint.
      if (this.sequenceEnd > 0 && noteStart >= this.sequenceEnd) {
        this.haltPlayback()
        return
      }

      this.scheduleElement(note, noteStart, noteEnd)

      this.index++
    }

    if (this.index >= this.loopEndIndex && this.loopCount < this.loopLimit) {
      if (this.loopEnd * this.playbackSpeed + this.loopTimeShift > endOfWindow) {
        // Exceeded current window.
        return
      }
      this.loopTimeShift += (this.loopEnd - this.loopStart) * this.playbackSpeed
      this.index = this.loopStartIndex
      this.loopCount++
      // Restart notes that overlapped loopStart (case (a) above).
      for (let i = 0; i < this.loopStartRestartNotes.length; i++) {
        const restartNote = this.loopStartRestartNotes[i]
        // Truncate if case (b) above applies.
        let restartNoteEnd = restartNote.noteTimes.endTime
        if (this.loopCount < this.loopLimit && restartNoteEnd > this.loopEnd) {
          restartNoteEnd = this.loopEnd
        }

        this.scheduleElement(
          restartNote.note,
          this.loopStart * this.playbackSpeed,
          restartNoteEnd * this.playbackSpeed
        )
      }
    }
  }
}

Sequencer.prototype.scheduleElement = function (
  this: SequencerType,
  note: Note,
  noteStart: number,
  noteEnd: number
) {
  this.scheduleNote(note, noteStart, noteEnd)
}

// // private
// // Helper for playNextWindow above.
// // noteStart and noteEnd are in reference frame (3).
Sequencer.prototype.scheduleNote = function (
  this: SequencerType,
  note: Note,
  noteStart: number,
  noteEnd: number
) {
  const timeOffset = this.synth.context.currentTime - this.startTime // reference frame (4)
  const onset = noteStart + this.loopTimeShift
  const duration = noteEnd - noteStart
  const modulationsList = null

  // Render the next note
  if (onset + duration > timeOffset) {
    // Only if it ends after the current time. (Ok if it starts before.)
    const inst = this.sequence.parts[note.channel].synthesizer
    let pitch = note.tunedOrdinal
    if (inst?.isPitched()) {
      pitch += this.pitchShift
    }
    // assign gain node for garbage collection (chrome42)
    const gainNode = inst?.scheduleNote(
      this.startTime + onset,
      duration,
      pitch,
      note.velocity,
      note.articulationValue || (note.articulation ? note.articulation.value : 'normal'),
      modulationsList
    )

    if (gainNode) {
      const release = inst?.release || 0
      this.scheduledNotes.push({
        gainNode: gainNode,
        endTime: this.startTime + onset + duration + release,
      })
    }
  }
}

export default Sequencer
